/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Revert while a draft is open** (`#1126`, Todd 2026-09-18: *"revert is still there in read-only
 * mode on details. It's not in edit mode — the user needs a way to revert their overrides to an
 * inherited field."*).
 *
 * Revert is a server write — it takes leaves out of this source's layer (`P3a`), and it cannot be
 * anything else without shipping the parent's values to a browser that may not be allowed to see them
 * (`revertArtifactPaths`). It was withheld in the editor because the **draft** still held the
 * override, so saving put it straight back. This closes that: once the source reloads, each reverted
 * row in the draft is replaced by the stored one, which now reads `inherited`. The rest of the draft
 * is untouched.
 *
 * Two consequences, both deliberate and both said on the control rather than left to be discovered:
 *
 * - **It takes effect now, not on Save**, and Cancel does not undo it — the same act as the revert
 *   beside a read-only row, which is why it is worded the same.
 * - **Rows are matched by key, never by id**: a projection mints fresh row ids on every read, so the
 *   stored row and the draft row never share one (`rowChangedFromStored`).
 *
 * Returns `null` where there is no revert to offer, so a caller can pass it straight to a control.
 */

import React from 'react';

export interface DraftRevertOptions<R> {
  onRevert: ((paths: string[]) => void | Promise<unknown>) | undefined;
  /** The stored rows, when a draft is open over them; `undefined` outside a draft. */
  baselineRows: readonly R[] | undefined;
  /** The draft's rows. */
  rows: readonly R[];
  keyOf: (row: R) => string;
  /** Which row keys a set of reverted paths belongs to. */
  keysOf: (paths: readonly string[]) => string[];
  /** Hand the reconciled rows back, with the keys that were reverted. */
  apply: (rows: R[], reverted: ReadonlySet<string>) => void;
}

/** What the stored rows say, without the row ids a projection mints fresh on every read. */
function contentKey<R>(rows: readonly R[] | undefined): string {
  return JSON.stringify(
    (rows ?? []).map((row) => {
      if (row === null || typeof row !== 'object' || !('id' in row)) return row;
      const { id: _id, ...rest } = row as Record<string, unknown>;
      return rest;
    }),
  );
}

export function useDraftRevert<R>({
  onRevert,
  baselineRows,
  rows,
  keyOf,
  keysOf,
  apply,
}: DraftRevertOptions<R>): ((paths: string[]) => void) | null {
  const baselineKey = contentKey(baselineRows);
  const pending = React.useRef<{ keys: Set<string>; baselineKey: string } | null>(null);

  // Read at reload time, not when the revert was pressed: the operator may have kept typing.
  const latest = React.useRef({ rows, baselineRows, keyOf, apply });
  React.useLayoutEffect(() => {
    latest.current = { rows, baselineRows, keyOf, apply };
  });

  React.useEffect(() => {
    const waiting = pending.current;
    if (!waiting || waiting.baselineKey === baselineKey) return;
    pending.current = null;
    const { rows: draft, baselineRows: stored, keyOf: key, apply: hand } = latest.current;
    const fresh = new Map((stored ?? []).map((row) => [key(row), row]));
    const next: R[] = [];
    for (const row of draft) {
      if (!waiting.keys.has(key(row))) next.push(row);
      else if (fresh.has(key(row))) next.push(fresh.get(key(row))!);
      // Reverted and absent from what is stored: the store is right, so the draft row goes.
    }
    for (const k of waiting.keys) {
      if (fresh.has(k) && !next.some((row) => key(row) === k)) next.push(fresh.get(k)!);
    }
    hand(next, waiting.keys);
  }, [baselineKey]);

  if (!onRevert) return null;
  if (baselineRows === undefined) return (paths) => void onRevert(paths);
  return (paths) => {
    pending.current = { keys: new Set(keysOf(paths)), baselineKey };
    void Promise.resolve(onRevert(paths)).catch(() => {
      // A failed revert changed nothing stored, so there is nothing to put back.
      pending.current = null;
    });
  };
}
